import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import {
  api,
  type IssuedInvitation,
  type PendingInvitation,
  type Person,
  type Role,
} from "../lib/api";
import { roleLabel, useSession } from "../lib/session";
import { useAction, useResource } from "../lib/useResource";
import { AppShell, ScreenHead } from "../shell/AppShell";
import {
  Button,
  Empty,
  Failed,
  Field,
  Label,
  Select,
  SkeletonRows,
} from "../components/primitives";
import { originFor, surfaceForHost } from "../lib/surface";
import { QrCode } from "../components/QrCode";
import { day } from "../lib/format";

/**
 * People — who exists, and how a new one comes to.
 *
 * **This screen used to be unable to show anybody.** It rendered pending
 * invitations and nothing else, so a person vanished from the interface at
 * the exact moment they accepted and became real. The user invited someone,
 * watched them accept, and then could not find them. The endpoint behind it
 * also excluded clients, because its only job had been filling a "choose a
 * Lead" picker.
 *
 * Both halves are fixed: `/api/people` returns everyone the caller may see
 * with enough state to act on, and this screen leads with them. Invitations
 * are now the secondary list, which is the right order — people who exist
 * matter more than people who have been asked.
 *
 * Inviting is Super Admin authority, said by `app_user_insert` in 004 and
 * re-checked by the definer functions in 023. The form being visible here is
 * courtesy; the refusal is the database's.
 */
export function People() {
  const [issued, setIssued] = useState<
    (IssuedInvitation & { fullName: string; email: string; role: Role }) | null
  >(null);

  /**
   * Reissued credentials live HERE, not in the row that produced them, and
   * that is the whole bug fix rather than a tidy-up.
   *
   * `PersonRow` used to hold them in its own state and then call
   * `people.reload()`. `useResource`'s reload sets the resource back to
   * `loading`, the list below only renders its rows when the state is
   * `ready`, so **every row unmounts** — taking the panel holding a
   * password that is shown exactly once with it. From the user's chair the
   * screen blinked and nothing happened: "it just refreshes."
   *
   * The invitation panel above never had this problem because it was
   * always here, outside the list. So is this now.
   */
  const [reissued, setReissued] = useState<Reissued | null>(null);
  const [showActiveAccounts, setShowActiveAccounts] = useState(false);
  const [showClosedAccounts, setShowClosedAccounts] = useState(false);

  const people = useResource<Person[]>(
    () => api.get<Person[]>("/api/people"),
    [],
  );
  const invitations = useResource<PendingInvitation[]>(
    () => api.get<PendingInvitation[]>("/api/invitations"),
    [],
  );
  const activePeople =
    people.resource.state === "ready"
      ? people.resource.data.filter((person) => !person.isSuspended)
      : [];
  const closedPeople =
    people.resource.state === "ready"
      ? people.resource.data.filter((person) => person.isSuspended)
      : [];

  return (
    <AppShell>
      <ScreenHead title="People" />

      {issued && (
        <IssuedLink invitation={issued} onDismiss={() => setIssued(null)} />
      )}

      {reissued && (
        <Reissue issued={reissued} onDismiss={() => setReissued(null)} />
      )}

      {/* Everyone, first. */}
      <section className="mt-8">
        {people.resource.state === "loading" && <SkeletonRows rows={4} />}
        {people.resource.state === "failed" && (
          <Failed
            message={people.resource.message}
            correlationId={people.resource.correlationId}
          />
        )}
        {people.resource.state === "ready" &&
          people.resource.data.length === 0 && (
            <Empty title="Nobody yet">
              Invite the first person below. There is no sign-up — the firm
              decides who exists.
            </Empty>
          )}
        {people.resource.state === "ready" &&
          people.resource.data.length > 0 &&
          activePeople.length === 0 && (
            <Empty title="No active accounts">
              Invite someone below or reopen a closed account.
            </Empty>
          )}
        {people.resource.state === "ready" &&
          activePeople.length > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setShowActiveAccounts((open) => !open)}
                aria-expanded={showActiveAccounts}
                aria-controls="active-accounts"
                className="label text-secondary transition-colors hover:text-ink"
              >
                <span
                  aria-hidden
                  className={
                    "mr-2 inline-block font-mono transition-transform duration-150 " +
                    (showActiveAccounts ? "rotate-90" : "")
                  }
                >
                  {"›"}
                </span>
                Active accounts ({activePeople.length})
              </button>

              <div id="active-accounts" hidden={!showActiveAccounts}>
                {activePeople.map((person) => (
                  <PersonRow
                    key={person.id}
                    person={person}
                    onChanged={people.reload}
                    onReissued={setReissued}
                  />
                ))}
              </div>
            </div>
          )}

        {people.resource.state === "ready" && closedPeople.length > 0 && (
          <div className="mt-6 border-t border-line pt-4">
            <button
              type="button"
              onClick={() => setShowClosedAccounts((open) => !open)}
              aria-expanded={showClosedAccounts}
              aria-controls="closed-accounts"
              className="label text-secondary transition-colors hover:text-ink"
            >
              <span
                aria-hidden
                className={
                  "mr-2 inline-block font-mono transition-transform duration-150 " +
                  (showClosedAccounts ? "rotate-90" : "")
                }
              >
                {"›"}
              </span>
              Closed accounts ({closedPeople.length})
            </button>

            <div id="closed-accounts" hidden={!showClosedAccounts}>
              {closedPeople.map((person) => (
                <PersonRow
                  key={person.id}
                  person={person}
                  onChanged={people.reload}
                  onReissued={setReissued}
                />
              ))}
            </div>
          </div>
        )}
      </section>

      <InviteForm
        onIssued={(v) => {
          setIssued(v);
          invitations.reload();
        }}
      />

      {/* People is the firm's roster — who has an account at all. Putting a
          person ON a piece of work is a property of that engagement, not of
          the person, so it lives there. Said out loud because "People" is
          exactly where somebody looks for it first. */}
      <section className="mt-12 border-t border-line pt-7">
        <Label as="h2">Assign people to an engagement</Label>
        <p className="mt-2 max-w-[62ch] text-sm text-secondary">
          Open an engagement to manage its team, consultant assignments and
          client access.
        </p>
        <Link
          to="/engagements"
          className="mt-4 inline-flex min-h-11 items-center justify-center border border-line-strong px-5 text-sm font-medium text-ink transition-colors hover:bg-ink/[0.06]"
        >
          Go to engagements
        </Link>
      </section>

      <section className="mt-12">
        <Label as="h2">Invited, not yet accepted</Label>

        {invitations.resource.state === "loading" && <SkeletonRows rows={2} />}
        {invitations.resource.state === "failed" && (
          <Failed
            message={invitations.resource.message}
            correlationId={invitations.resource.correlationId}
          />
        )}
        {invitations.resource.state === "ready" &&
          invitations.resource.data.length === 0 && (
            <Empty title="Nobody is waiting">
              Everyone invited has set up their account.
            </Empty>
          )}
        {invitations.resource.state === "ready" &&
          invitations.resource.data.map((inv) => (
            <PendingRow
              key={inv.id}
              invitation={inv}
              onChanged={() => {
                invitations.reload();
                people.reload();
              }}
            />
          ))}
      </section>
    </AppShell>
  );
}

/**
 * One person, and the three things that can be done about them.
 *
 * The screen could list everyone and do nothing about any of them:
 * `app.suspend_user` has existed since migration 011 — with its own Super
 * Admin check and session revocation — and had no caller anywhere.
 *
 * **Close, not delete.** There is no delete button because there is no
 * delete. `app_user` is referenced by every audit row, task assignment,
 * submitted version, invoice and RAID entry in the system; removing a
 * person would either break all of that or quietly rewrite history to say
 * nobody did it. A closed account cannot sign in, loses every session, and
 * reaches nothing — as shut as deletion, with the record still true.
 */
function PersonRow({
  person,
  onChanged,
  onReissued,
}: {
  person: Person;
  onChanged: () => void;
  /** Handed up, because reloading the list unmounts this row. */
  onReissued: (issued: Reissued) => void;
}) {
  const [confirming, setConfirming] = useState(false);

  const close = useAction(async () => {
    await api.post(`/api/people/${person.id}/suspend`, {});
    setConfirming(false);
    onChanged();
  });

  const reopen = useAction(async () => {
    await api.post(`/api/people/${person.id}/restore`, {});
    onChanged();
  });

  const reissue = useAction(async (what: "password" | "mfa" | "both") => {
    const out = await api.post<Omit<Reissued, "role">>(`/api/people/${person.id}/reissue`, {
      password: what !== "mfa",
      mfa: what !== "password",
    });
    // Up first, then reload. The reload unmounts this component, so
    // anything held locally at this point is gone before it is read.
    onReissued({ ...out, role: person.role });
    onChanged();
  });

  const busy = reissue.pending || close.pending || reopen.pending;

  return (
    <div className="border-b border-line py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="min-w-0">
          <p className="text-base text-ink">
            {person.fullName}
            {person.isSuspended && (
              <span className="ml-2 text-sm text-danger">closed</span>
            )}
          </p>
          <p className="mt-1 break-all text-sm text-secondary">{person.email}</p>
        </div>

        <div className="flex flex-wrap items-baseline gap-4 text-sm text-secondary">
          <span className="label">{roleLabel(person.role)}</span>
          {/* Worth showing: MFA is mandatory for the top three roles, so an
              un-enrolled Lead is an account that cannot sign in at all. */}
          <span className={person.mfaEnrolled ? "" : "text-danger"}>
            {person.mfaEnrolled ? "MFA on" : "no MFA"}
          </span>
          <span>joined {day(person.createdAt)}</span>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
        {person.isSuspended ? (
          <Button tone="secondary" loading={busy} onClick={() => void reopen.invoke()}>
            Reopen the account
          </Button>
        ) : (
          <>
            <Button tone="quiet" loading={busy} onClick={() => void reissue.invoke("password")}>
              New password
            </Button>
            <Button tone="quiet" loading={busy} onClick={() => void reissue.invoke("mfa")}>
              New authenticator
            </Button>
            {/* Two acts, one button, for the case that actually happens:
                somebody has lost the phone and forgotten the password. */}
            <Button tone="quiet" loading={busy} onClick={() => void reissue.invoke("both")}>
              Password + authenticator
            </Button>
            {!confirming ? (
              <Button tone="quiet" onClick={() => setConfirming(true)}>
                Close the account
              </Button>
            ) : (
              <>
                <span className="text-ink">
                  Close it? They will be signed out immediately.
                </span>
                <Button tone="danger" loading={busy} onClick={() => void close.invoke()}>
                  Yes, close it
                </Button>
                <Button tone="quiet" onClick={() => setConfirming(false)}>
                  Cancel
                </Button>
              </>
            )}
          </>
        )}
      </div>

      {(reissue.refusal || close.refusal || reopen.refusal) && (
        <p className="mt-3 border-l-2 border-danger pl-4 text-sm text-ink">
          {(reissue.refusal ?? close.refusal ?? reopen.refusal)?.message}
        </p>
      )}

    </div>
  );
}

interface Reissued {
  email: string;
  fullName: string;
  role: Role;
  password?: string;
  mfaSecret?: string;
  otpauthUrl?: string;
  /** Whether the platform mailed it. False when both were reissued. */
  emailed?: boolean;
}

/**
 * What was just issued, shown once — at the top of the page.
 *
 * **Not inside the row that produced it**, which is the bug this fixes.
 * Reissuing reloads the list, `useResource` flips the resource back to
 * `loading` while it refetches, and the rows unmount. A panel held in a
 * row's own state was therefore destroyed a fraction of a second after it
 * appeared, which looked exactly like the button doing nothing.
 *
 * Nothing here is recoverable. The password is hashed before it reaches
 * the database and the secret lives in a table nothing can read back, so
 * closing this panel is the last time anyone sees either.
 */
function Reissue({
  issued,
  onDismiss,
}: {
  issued: Reissued;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const both = Boolean(issued.password) && Boolean(issued.mfaSecret);

  const block = [
    `Platform  ${originFor(issued.role === "client" ? "client" : "firm")}`,
    `Email     ${issued.email}`,
    issued.password ? `Password  ${issued.password}` : null,
    issued.mfaSecret ? `Auth key  ${issued.mfaSecret}` : null,
    issued.mfaSecret
      ? "          Add that key to an authenticator as a manual entry."
      : null,
  ]
    .filter(Boolean)
    .join("\n");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(block);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // The text is on screen and selectable; a clipboard refusal is a
      // convenience failing, not the feature failing.
      setCopied(false);
    }
  };

  return (
    <section className="mt-8 border border-line-strong p-6">
      <Label as="h2">New credentials for {issued.fullName}</Label>

      {issued.emailed ? (
        <p className="mt-3 max-w-[58ch] text-base text-ink">
          Emailed to <span className="font-medium">{issued.email}</span>.
        </p>
      ) : (
        <p className="mt-3 max-w-[58ch] text-base text-ink">
          Not emailed — give these to {issued.fullName.split(" ")[0]}{" "}
          yourself. No email was sent. Check their email address or share
          these details with them directly.
        </p>
      )}

      <p className="mt-2 max-w-[58ch] text-sm text-secondary">
        {both ? (
          <>
            A new password and authenticator setup were emailed together.
          </>
        ) : issued.password ? (
          <>
            Only the password changed. Their current authenticator still works.
          </>
        ) : (
          <>
            Only the authenticator changed. Their current password still works.
          </>
        )}
      </p>

      <p className="mt-4 max-w-[58ch] text-sm text-secondary">
        Shown <strong className="text-ink">once</strong>. Copy these details
        now. They cannot be displayed again; if they are lost, issue new ones.
      </p>

      <pre className="mt-4 overflow-x-auto whitespace-pre-wrap break-all bg-control p-4 font-mono text-sm text-ink">
        {block}
      </pre>

      {issued.otpauthUrl && (
        <div className="mt-5">
          <p className="label">Or scan this</p>
          <div className="mt-3">
            <QrCode
              value={issued.otpauthUrl}
              label={`Authenticator code for ${issued.email}`}
            />
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-3">
        <Button tone="quiet" onClick={() => void copy()}>
          {copied ? "Copied" : "Copy"}
        </Button>
        <Button tone="primary" onClick={onDismiss}>
          Done
        </Button>
      </div>
    </section>
  );
}

const ROLES: Role[] = [
  "consultant",
  "project_lead",
  "principal_partner",
  "client",
  "super_admin",
];

function InviteForm({
  onIssued,
}: {
  onIssued: (
    v: IssuedInvitation & { fullName: string; email: string; role: Role },
  ) => void;
}) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("consultant");

  const { invoke, pending, refusal } = useAction(async () => {
    const result = await api.post<IssuedInvitation>("/api/invitations", {
      fullName,
      email,
      role,
    });
    onIssued({ ...result, fullName, email, role });
    setFullName("");
    setEmail("");
    setRole("consultant");
  });

  const submit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      void invoke();
    },
    [invoke],
  );

  return (
    <form onSubmit={submit} className="mt-12 border-t border-line pt-7">
      <Label as="h2">Invite someone</Label>

      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        <Field
          label="Full name"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          required
          autoComplete="off"
        />
        <Field
          label="Email"
          type="email"
          hint="This is also how they sign in."
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="off"
        />
      </div>

      <div className="mt-5 max-w-sm">
        <Select
          label="Role"
          hint="Decides what they see. It cannot be changed from this screen afterwards."
          value={role}
          onChange={(e) => setRole(e.target.value as Role)}
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {roleLabel(r)}
            </option>
          ))}
        </Select>
      </div>

      {refusal && (
        <p className="mt-5 border-l-2 border-danger pl-4 text-sm text-ink">
          {refusal.message}
        </p>
      )}

      <Button tone="primary" type="submit" className="mt-7" loading={pending}>
        Create invitation
      </Button>
    </form>
  );
}

/**
 * The link, shown once.
 *
 * Built here because the correct hostname depends on the role: a client
 * belongs at the portal name and everyone else at the firm's, and a client
 * sent to the firm's address meets the "wrong door" screen before they have
 * even signed in.
 */
function IssuedLink({
  invitation,
  onDismiss,
}: {
  invitation: IssuedInvitation & { fullName: string; email: string; role: Role };
  onDismiss: () => void;
}) {
  const { me } = useSession();
  const [copied, setCopied] = useState<"link" | "message" | null>(null);

  const base =
    surfaceForHost() === "either"
      ? window.location.origin
      : originFor(invitation.role === "client" ? "client" : "firm");

  // A fragment, not a path segment. Fragments are never sent to a server, so
  // this live credential stays out of access logs and referrer headers.
  const link = `${base}/invite#${invitation.token}`;

  const isClient = invitation.role === "client";
  const firstName = invitation.fullName.trim().split(/\s+/)[0] ?? "there";

  const subject = isClient
    ? "Your client portal — Arikekpar & Company"
    : "Your access to the Arikekpar & Company platform";

  /**
   * The letter, written out so nobody has to compose it again.
   *
   * The thing this replaces is a person pasting a bare URL into Gmail and
   * typing the surrounding explanation from memory — differently every
   * time, and usually without the two facts that stop the support request:
   * that the link works once, and that an authenticator is not optional.
   *
   * Plain text on purpose. It is going to be pasted into whatever mail
   * client the firm actually uses, and anything cleverer arrives as
   * markup nobody asked for.
   */
  const message = [
    `Dear ${firstName},`,
    "",
    isClient
      ? "Your portal for our work together is ready. You can see progress, read anything we have shared with you, and sign off what needs your approval."
      : "Your account on the Arikekpar & Company platform is ready.",
    "",
    "Open this link to set it up:",
    link,
    "",
    `The link works once and expires on ${day(invitation.expiresAt)}.`,
    "",
    "You will be asked to add the account to an authenticator app — Google Authenticator, Authy or 1Password all work. There is a code on screen to scan. You will need the six-digit number it generates every time you sign in, so please do not skip that step.",
    "",
    "If the link has expired or does not work, reply to this message and I will send another.",
    "",
    me ? `— ${me.fullName}` : "— Arikekpar & Company",
    me ? "Arikekpar & Company" : "",
  ]
    .join("\n")
    // A signature with no name leaves a trailing blank line, and three
    // blank lines anywhere read as a draft somebody abandoned.
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  const copy = async (what: "link" | "message") => {
    try {
      await navigator.clipboard.writeText(what === "link" ? link : message);
      setCopied(what);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      // The text is selectable; nothing useful to report.
    }
  };

  // Opens whatever handles mail on this machine, already addressed. The
  // token travels in a mailto: URL rather than over the network — the
  // browser hands it to the mail client — so it goes no further than the
  // clipboard route already does.
  const mailto =
    `mailto:${encodeURIComponent(invitation.email)}` +
    `?subject=${encodeURIComponent(subject)}` +
    `&body=${encodeURIComponent(message)}`;

  return (
    <section className="mt-8 border border-line-strong p-6">
      <Label as="h2">Invitation sent</Label>

      <p className="mt-3 max-w-[58ch] text-base text-ink">
        We emailed {invitation.fullName} at{" "}
        <span className="font-medium">{invitation.email}</span>.
      </p>

      <p className="mt-2 max-w-[58ch] text-sm text-secondary">
        It should arrive within a minute. If they do not receive it, you can
        copy the invitation below and send it yourself.
      </p>

      <p className="mt-4 max-w-[58ch] text-sm text-secondary">
        Save the link before leaving this screen. For security, it is shown{" "}
        <strong className="text-ink">only once</strong>, can be used once,
        and expires {day(invitation.expiresAt)}.
      </p>

      <pre className="mt-3 max-h-80 overflow-y-auto whitespace-pre-wrap break-words bg-control p-4 font-sans text-sm leading-relaxed text-ink">
        {message}
      </pre>

      <div className="mt-6 flex flex-wrap gap-3">
        <Button tone="quiet" onClick={() => void copy("message")}>
          {copied === "message" ? "Copied" : "Copy invitation"}
        </Button>
        <a
          href={mailto}
          className="inline-flex min-h-11 items-center justify-center border border-ink px-5 text-sm text-ink transition-colors duration-150 hover:bg-ink hover:text-canvas"
        >
          Open in your email
        </a>
        <Button tone="quiet" onClick={() => void copy("link")}>
          {copied === "link" ? "Copied" : "Just the link"}
        </Button>
        <Button tone="primary" onClick={onDismiss}>
          Done
        </Button>
      </div>

      <p className="mt-4 max-w-[58ch] text-xs text-secondary">
        The invitation lets them create their password and set up their
        authenticator. If the link expires or is lost, revoke it and send a
        new invitation.
      </p>
    </section>
  );
}

function PendingRow({
  invitation,
  onChanged,
}: {
  invitation: PendingInvitation;
  onChanged: () => void;
}) {
  const { invoke, pending } = useAction(async () => {
    await api.post(`/api/invitations/${invitation.id}/revoke`, {});
    onChanged();
  });

  return (
    <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-line py-4">
      <div className="min-w-0">
        <p className="text-base text-ink">
          {invitation.fullName}{" "}
          <span className="text-secondary">· {roleLabel(invitation.role)}</span>
        </p>
        <p className="mt-1 break-all text-sm text-secondary">
          {invitation.email}
        </p>
      </div>

      <div className="flex items-center gap-4">
        <p className="text-sm text-secondary">
          {invitation.expired
            ? "Expired or revoked"
            : `Expires ${day(invitation.expiresAt)}`}
        </p>
        {!invitation.expired && (
          <Button tone="quiet" loading={pending} onClick={() => void invoke()}>
            Revoke
          </Button>
        )}
      </div>
    </div>
  );
}
